import { useState } from "react";
import { Phone } from "lucide-react";
import { resort } from "@/data/resort";
import { cn } from "@/lib/utils";
import { useBooking } from "../booking-context";
import { ResortButton, ResortLink } from "../resort-button";
import { Eyebrow, MaskReveal, Reveal } from "../reveal";

const preferences = ["Pull Through", "Back In", "No Preference"] as const;
const stayTypes = ["Daily", "Weekly", "Monthly"] as const;

export function AccommodationsFitTool() {
  const { openBooking } = useBooking();
  const [length, setLength] = useState(45);
  const [preference, setPreference] = useState<(typeof preferences)[number]>("No Preference");
  const [stayType, setStayType] = useState<(typeof stayTypes)[number]>("Daily");
  const [submitted, setSubmitted] = useState(false);

  return (
    <section id="fit" aria-labelledby="fit-heading" className="bg-background py-16 md:py-20">
      <div className="shell grid gap-10 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Traveling with a Big Rig?</Eyebrow>
          </Reveal>
          <h2 id="fit-heading" className="display-lg mt-6">
            <MaskReveal>Let&apos;s Find the</MaskReveal>
            <MaskReveal delay={0.08}>Right Space</MaskReveal>
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-muted-foreground">
              Long RV sites and 50 amp RV sites help make Auburn RV Resort a big rig friendly RV
              park — use this tool for guidance, then confirm the best available site with our team.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.08} className="lg:col-span-7">
          <form
            className="border border-border bg-card p-8 md:p-12"
            onSubmit={(e) => {
              e.preventDefault();
              setSubmitted(true);
            }}
          >
            <div>
              <label
                htmlFor="acc-rv-length"
                className="eyebrow flex justify-between text-muted-foreground"
              >
                <span>RV Length</span>
                <span className="text-foreground">{length} ft</span>
              </label>
              <input
                id="acc-rv-length"
                type="range"
                min={20}
                max={120}
                value={length}
                onChange={(e) => {
                  setLength(Number(e.target.value));
                  setSubmitted(false);
                }}
                className="mt-5 w-full accent-primary"
              />
              <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                <span>20 ft</span>
                <span>120 ft</span>
              </div>
            </div>

            <fieldset className="mt-10">
              <legend className="eyebrow text-muted-foreground">Site Preference</legend>
              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                {preferences.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      setPreference(opt);
                      setSubmitted(false);
                    }}
                    aria-pressed={preference === opt}
                    className={cn(
                      "eyebrow border px-5 py-4 transition-colors",
                      preference === opt
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border text-muted-foreground hover:border-foreground hover:text-foreground",
                    )}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="mt-10">
              <legend className="eyebrow text-muted-foreground">Stay Type</legend>
              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                {stayTypes.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      setStayType(opt);
                      setSubmitted(false);
                    }}
                    aria-pressed={stayType === opt}
                    className={cn(
                      "eyebrow border px-5 py-4 transition-colors",
                      stayType === opt
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border text-muted-foreground hover:border-foreground hover:text-foreground",
                    )}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </fieldset>

            <ResortButton type="submit" variant="solid" className="mt-10 w-full sm:w-auto">
              See My Options
            </ResortButton>

            {submitted && (
              <div role="status" className="animate-fade-in mt-10 border-t border-border pt-10">
                <h3 className="display-md">We may have options for your rig.</h3>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
                  Auburn RV Resort offers sites in a wide range of lengths, including some spaces
                  reaching approximately 120 feet. A {length} ft RV
                  {preference !== "No Preference"
                    ? ` preferring a ${preference.toLowerCase()} site`
                    : ""}{" "}
                  for a {stayType.toLowerCase()} stay may be a fit — availability and site
                  compatibility depend on your RV configuration and travel dates. Contact the resort
                  to confirm the best site for your rig.
                </p>
                <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <ResortButton
                    type="button"
                    variant="gold"
                    onClick={openBooking}
                    className="w-full sm:w-auto"
                  >
                    Check Availability
                  </ResortButton>
                  <ResortLink
                    variant="outline"
                    href={resort.phoneHref}
                    className="w-full sm:w-auto"
                  >
                    <Phone className="size-4" strokeWidth={1.6} /> Call to Confirm
                  </ResortLink>
                </div>
              </div>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}
